type ErrorLevel = 'warning' | 'error' | 'critical';
type ErrorReporter = (error: unknown, context: ErrorContext) => void;

interface ErrorContext {
  level: ErrorLevel;
  message: string;
  operation?: string;
  retry?: () => void;
}

let externalReporter: ErrorReporter | null = null;

/** Register an external error reporter (e.g., Sentry) */
export function registerErrorReporter(reporter: ErrorReporter): void {
  externalReporter = reporter;
}

/** Report error to external service if configured */
export function reportError(error: unknown, context: ErrorContext): void {
  if (externalReporter) {
    externalReporter(error, context);
  }
}

/** Centralized error handler */
export function handleError(error: unknown, context: ErrorContext): void {
  const isDev = import.meta.env.DEV;
  const _errorMessage = error instanceof Error ? error.message : String(error);

  // Always log in dev
  if (isDev) {
    console.error(`[${context.level.toUpperCase()}] ${context.message}:`, error);
  }

  // Report to external service
  reportError(error, context);

  // Show toast notification to user
  showErrorToast(context.message, context.level, context.retry);
}

// --- Simple toast notification system ---
interface Toast {
  id: string;
  message: string;
  level: ErrorLevel;
  retry?: () => void;
}

type ToastListener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
const listeners = new Set<ToastListener>();
let toastIdCounter = 0;

function notifyListeners(): void {
  listeners.forEach(listener => listener([...toasts]));
}

export function subscribeToToasts(listener: ToastListener): () => void {
  listeners.add(listener);
  listener([...toasts]);
  return () => listeners.delete(listener);
}

export function dismissToast(id: string): void {
  toasts = toasts.filter(t => t.id !== id);
  notifyListeners();
}

function showErrorToast(message: string, level: ErrorLevel, retry?: () => void): void {
  const id = `toast-${++toastIdCounter}`;
  const toast: Toast = { id, message, level, retry };
  toasts = [...toasts, toast];
  notifyListeners();

  // Auto-dismiss after 8 seconds for warnings, 12 for errors
  const duration = level === 'warning' ? 8000 : level === 'critical' ? 15000 : 12000;
  setTimeout(() => dismissToast(id), duration);
}

export type { Toast, ErrorLevel, ErrorContext };
